/* eslint-disable no-console */
/* eslint-disable newline-before-return */
import {
  IExistingUpload,
  isExistingUpload,
  isUploadWithFile,
  IUpload,
  IUploadWithFile,
} from '@stormwater-software/lib-form';
import instance, { xhrService } from '../xhrService';
import db, { offlineImageCache } from '../../db/db';
import uploadApi, { fileToBase64 } from './upload';
import { v4 as uuidv4 } from 'uuid';
import { getDateOnly } from '../../utils/date';
import {
  isNetworkFailure,
  withOfflineFallback,
  withOfflineWrite,
} from '../offlineFallback';
import { syncAfterOnlineSave } from '../offlineSyncSend';
import { FINDING_PHOTO_MAX_SHORT_EDGE, resizePhoto } from '../resizePhoto';
import {
  backgroundPhotoUploads,
  UploadProgress,
  assertOnline,
} from '../backgroundPhotoUploads';
import { waitForForegroundIdle } from '../foregroundRequests';

export type Finding = {
  id: number;
  type: string;
  date_completed: Date | null;
  date_initiated: Date | null;
};

/**
 * Findings by inspection
 */
const url = (a: number) => `/api/inspections/${a}/findings`;

function generateId(id: number, findings: any) {
  if (id !== undefined && id !== null && id !== 0) return id;
  if (findings && findings.length > 0) {
    return findings[findings.length - 1].id + Math.floor(Math.random() * 100);
  }
  return Math.floor(Math.random() * 1000);
}

async function uploadImagesOffline<T extends { id: number }>(
  finding: T,
  newUploads: IUploadWithFile[] = [],
  online: boolean = false,
  // SW2-2136: stored with the queued photo so the sync uploads it as what it is. Photos taken
  // offline used to carry no type and synced as 'initial', so closure photos printed as Initial
  // and could never meet a closure-photo requirement.
  photoType: 'initial' | 'progress' | 'closure' = 'initial',
): Promise<T & { finding_uploads: any[] }> {
  const findingObj = await db.findings.get(finding.id).catch((error: any) => {
    console.log('Error getting offline finding', error);
  });

  if (!findingObj) {
    // Expected when the inspection was never synced for offline use — the
    // offline mirror is skipped and the online flow is unaffected.
    console.log(
      'Offline mirror: finding not in local db, skipping offline upload:',
      finding.id,
    );
    return { ...finding, finding_uploads: [] };
  }

  const newImgArr: any = [];

  for (const upload of newUploads) {
    const {
      file,
      name,
      annotations = [],
      rotation = 0,
      type,
      timestamp,
      gps_latitude,
      gps_longitude,
    } = upload;
    const guid = upload.guid ? upload.guid : uuidv4();

    const base64File = await fileToBase64(file);

    // SW2-2449: the photo's own date (EXIF) or none. This used to fall back to the time the
    // photo was queued, which saved as if it were when the photo was taken, and an API without
    // the SW2-2449 fix rejected that ISO string with a 500 'Invalid date'.
    const timeStamp = timestamp || null;
    const gpsData =
      gps_latitude && gps_longitude
        ? { lat: gps_latitude, lon: gps_longitude }
        : null;

    newImgArr.push({
      annotations: annotations,
      createdAt: new Date().toISOString(),
      deletedAt: null,
      finding_id: finding.id,
      is_completed: photoType === 'closure',
      photo_type: photoType,
      timestamp: timeStamp,
      gpsData: gpsData,
      id: guid,
      upload: {
        GUID: guid,
        mime_type: type,
        name: name,
        timestamp: timeStamp,
        rotation,
        gps_latitude: gps_latitude,
        gps_longitude: gps_longitude,
        archived_at: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        deletedAt: null,
      },
    });

    // `payload.file` carries the upload metadata only. The raw File is not
    // persisted: iOS stores it as a reference into Safari's temp area, and once
    // iOS reclaims that file the row still reads back as a File with no bytes
    // behind it, so the sync sent an empty multipart body and the API answered
    // 400 "No file attached". The Base64 copy below is the durable one and sync
    // rebuilds the File from it.
    const syncObj = {
      synced: false,
      payload: {
        file: {
          annotations,
          rotation,
          type,
          name,
          gpsData,
          timestamp: timeStamp,
        },
        payload_id: finding.id,
        photo_type: photoType,
      },
    };

    if (online) {
      syncObj.synced = true;
    }

    // `put`, not `add` (keyed by guid): writing the same photo again — a Retry repairing a copy whose
    // finding update failed after this row was written — must not be refused as a duplicate.
    await db.images.put({
      guid: guid,
      file: base64File,
      type: type,
      name: name,
      payload_id: finding.id,
      photo_type: photoType,
      sync: {
        ...syncObj,
      },
    });

    offlineImageCache[guid] = base64File;
  }

  // And never list a photo twice: a repeated write replaces its earlier entry.
  const newIds = new Set(newImgArr.map((upload: any) => upload.id));
  const keptUploads = (findingObj.finding_uploads || []).filter(
    (upload: any) => !newIds.has(upload.id),
  );

  await db.findings.update(finding.id, {
    finding_uploads: [
      ...keptUploads,
      ...newImgArr.map((upload: any) => ({
        ...upload,
        id: upload.id,
        finding_id: finding.id,
      })),
    ],
  });

  return {
    ...finding,
    finding_uploads: [
      ...(findingObj.finding_uploads ? findingObj.finding_uploads : []),
      ...newImgArr.map((upload: any) => ({
        ...upload,
        id: upload.id,
        finding_id: finding.id,
      })),
    ],
  };
}

/**
 * SW2-2136 — photos that already reached the server, by guid → their finding_upload id, so a
 * retry after a refused save (a signed inspection, an invalid type, a closure-photo refusal)
 * sends them as existing instead of uploading them again.
 *
 * Recorded here, NOT written onto the photo: these are the form's own objects, and lib-form's
 * image field accepts a photo with a file and no id (new) or an id and no file (existing), and
 * throws on anything else. Setting `id` on a photo that still held its file crashed Finding
 * Detail with "`value` must implement IUpload[]".
 */
const uploadedPhotoIds = new Map<string, number>();

/**
 * SW2-2458 — photos on the server whose local (IndexedDB) copy then failed, by guid. A Retry
 * re-sends nothing for them (see uploadedPhotoIds), so it repairs the copy from here instead;
 * otherwise a downloaded inspection would stay without the photo.
 */
const unmirroredPhotos = new Set<string>();

/**
 * SW2-2458 — existing photos (form objects) whose edits — rotation, new annotations, deletion — a
 * background job has already sent. The server applies a rotation and an id-less annotation again
 * each time it receives them, so a Retry (after a later step failed) sends these without the edits.
 */
const appliedExistingEdits = new WeakSet<object>();

function markUploaded(images: any[] | undefined, rows: any[] | undefined) {
  (images || []).forEach((image: any) => {
    if (image.id || !image.guid) return;

    const row = (rows || []).find(
      (upload: any) => upload.upload && upload.upload.GUID === image.guid,
    );

    if (row) uploadedPhotoIds.set(image.guid, row.id);
  });
}

/**
 * Rejects with a user-readable error when a selected photo can no longer be
 * read from disk (0-byte or unreadable File handles produce empty uploads).
 */
async function assertFilesReadable(newUploads: IUploadWithFile[]) {
  for (const upload of newUploads) {
    const file: any = upload.file;

    // Offline-sync flows can carry base64 strings instead of File objects
    if (typeof file === 'string') continue;

    let unreadable = !file || file.size === 0;

    if (!unreadable) {
      try {
        await file.slice(0, 1).arrayBuffer();
      } catch {
        unreadable = true;
      }
    }

    if (unreadable) {
      throw new Error(
        `The photo "${
          upload.name || (file && file.name) || 'selected'
        }" could not be read. Please re-select it and try again.`,
      );
    }
  }
}

/**
 * SW2-2440: copies of the new uploads with their photo resized. Copies, because
 * these are the form's own objects (see uploadedPhotoIds).
 */
async function resizeNewUploads(
  uploads: IUploadWithFile[],
): Promise<IUploadWithFile[]> {
  const resized: IUploadWithFile[] = [];

  for (const upload of uploads) {
    const file = await resizePhoto(upload.file, {
      maxShortEdge: FINDING_PHOTO_MAX_SHORT_EDGE,
    });

    resized.push(file === upload.file ? upload : { ...upload, file });
  }

  return resized;
}

async function uploadImages<T extends { id: number }>(
  finding: T,
  images: IUpload[] = [],
  online: boolean = false,
  photoType: 'initial' | 'progress' | 'closure' = 'initial',
  // SW2-2458: progress for the background upload notice, and
  // `localOnlyWhenOffline` so a background job fails (and offers Retry) on a
  // dropped connection instead of falling into a queue that may not exist.
  { onProgress, localOnlyWhenOffline, existingSynced }: BackgroundableOpts = {},
): Promise<T & { finding_uploads: any[] }> {
  if (images.length === 0) {
    return { ...finding, finding_uploads: [] };
  }

  // A background job's Retry: photos it already uploaded go as existing, without the rotation and
  // annotations the server applied when it inserted them (see extractUploads).
  const [selectedUploads, existingUploads] = extractUploads(images, { dropAppliedEdits: !!onProgress });

  await assertFilesReadable(selectedUploads);

  // SW2-2440: send (and keep offline) a resized copy. GPS and the EXIF date were
  // already read from the original file when the photo was added.
  const newUploads = await resizeNewUploads(selectedUploads);

  // SW2-2321: was a bare `navigator.onLine` pre-check. It reports true after an
  // offline reload, so the upload went to the network, failed, and never reached
  // uploadImagesOffline — the inspector's photos were dropped with no error.
  //
  // The post-success work below (validating the returned rows and assigning
  // GUIDs) stays inside the request: a throw from there is not network-shaped, so
  // withOfflineWrite rethrows rather than queueing uploads the server already has.
  // SW2-2458: only a background job's upload reports progress (`onProgress`; the save a form
  // waits on passes `localOnlyWhenOffline` too, so that cannot tell them apart). On a cellular
  // uplink one request carrying every photo holds back every other request until it has finished,
  // so a background job sends one photo per request, steps aside for foreground requests before
  // each, and records each photo as uploaded as soon as it lands (a Retry then sends only the
  // rest). Edits to existing uploads (rotations, annotations, deletions) go with the LAST request:
  // rotations and new annotations are not idempotent on the server, so an early request must not
  // apply them for a Retry to apply again. A form that waits on the upload keeps the single request.
  const background = !!onProgress;
  const batches: IUploadWithFile[][] = background
    ? newUploads.length
      ? newUploads.map((upload) => [upload])
      : [[]]
    : [newUploads];

  // A Retry: first repair the local copies that failed after their photos reached the server.
  if (background && online) {
    const unmirrored = images.filter(
      (image: any) => isUploadWithFile(image) && image.guid && unmirroredPhotos.has(image.guid),
    ) as IUploadWithFile[];

    if (unmirrored.length > 0) {
      await uploadImagesOffline(finding, await resizeNewUploads(unmirrored), true, photoType);
      unmirrored.forEach((image) => unmirroredPhotos.delete(image.guid as string));
    }
  }

  // Everything is on the server already (a Retry after the local copy failed) and the existing
  // photos were synced by the save: there is nothing left to send.
  if (background && existingSynced && newUploads.length === 0) {
    return { ...finding, finding_uploads: [] };
  }

  // Photos of this call already on the server from an earlier attempt (sent as existing now).
  const alreadyLanded = images.filter(
    (image: any) => isUploadWithFile(image) && image.guid && uploadedPhotoIds.has(image.guid),
  ).length;

  return withOfflineWrite<T & { finding_uploads: any[] }>(
    async () => {
      let allUploads: any[] = [];
      let sent = 0;

      try {
        for (let b = 0; b < batches.length; b++) {
          const batch = batches[b];

          if (background) {
            await waitForForegroundIdle();
            // The wait can be long enough for the connection to drop: fail as not-sent (retried
            // once back online) rather than as a request whose answer was lost.
            assertOnline();
          }

          allUploads =
            (await uploadApi.finding.update(
              finding.id,
              batch,
              b === batches.length - 1 && !existingSynced ? existingUploads : [],
              photoType,
              batches.length > 1
                ? scaledProgress(onProgress, sent, batch.length, newUploads.length)
                : onProgress,
              { background },
            )) || [];

          // The server has applied the existing photos' edits: a Retry must not send them again.
          if (background && b === batches.length - 1 && !existingSynced) {
            images.forEach((image) => {
              if (isExistingUpload(image)) appliedExistingEdits.add(image);
            });
          }

          // The server returns ALL of the finding's uploads ordered created_at ASC,
          // so the rows for the files we just sent are the tail of the list. Validate
          // instead of blindly indexing from 0 so a dropped upload surfaces as an
          // error rather than silently mis-assigning GUIDs.
          if (batch.length > 0) {
            const newRows = allUploads.slice(Math.max(0, allUploads.length - batch.length));

            if (
              newRows.length < batch.length ||
              newRows.some((row: any) => !row || !row.upload || !row.upload.GUID)
            ) {
              throw new Error(
                'The server did not confirm all uploaded photos. Please re-open the finding and re-upload any missing photos.',
              );
            }

            for (let i = 0; i < batch.length; i++) {
              // Also onto the form's own object: markUploaded reads the guid from it.
              batch[i].guid = newRows[i].upload.GUID;
              selectedUploads[sent + i].guid = newRows[i].upload.GUID;
            }

            // SW2-2458: recorded before the local mirror below. If that fails, the
            // server already has these photos, and a Retry must send them as
            // existing uploads rather than upload them again.
            markUploaded(selectedUploads.slice(sent, sent + batch.length), allUploads);

            // Mirrored per photo: a Retry sends a landed photo as existing, so a mirror deferred to
            // the end would never get it. Tracked until it succeeds, so a Retry can repair it.
            if (online && background) {
              const guids = batch.map((upload) => upload.guid as string);

              guids.forEach((guid) => unmirroredPhotos.add(guid));
              await uploadImagesOffline(finding, batch, true, photoType);
              guids.forEach((guid) => unmirroredPhotos.delete(guid));
            }
          }

          sent += batch.length;
        }
      } catch (err) {
        // Tell the notice how many of the job's photos are on the server despite the failure.
        if (background && err && typeof err === 'object' && alreadyLanded + sent > 0) {
          err.landedPhotos = (err.landedPhotos || 0) + alreadyLanded + sent;
        }

        throw err;
      }

      const uploads = { ...finding, finding_uploads: allUploads };

      if (online && !background) {
        await uploadImagesOffline(finding, newUploads, true, photoType);
      }

      return uploads;
    },
    offlineQueueOr({ localOnlyWhenOffline }, () =>
      uploadImagesOffline(finding, newUploads, false, photoType),
    ),
    { localOnlyWhenOffline },
  );
}

/**
 * SW2-2458. `onProgress` and `localOnlyWhenOffline` are what a background job
 * passes; `backgroundPhotos` is what a form passes to save now and upload later.
 */
type BackgroundableOpts = {
  onProgress?: UploadProgress;
  localOnlyWhenOffline?: boolean;
  /**
   * The save before this upload already synced the existing photos (their rotations, annotations
   * and deletions), so only the new photos are sent. Sending the existing ones again would rotate
   * them a second time and duplicate their new annotations; the API never deletes a photo for
   * being left out of the list (`syncFindingUploads` deletes only `deleted: true`).
   */
  existingSynced?: boolean;
};

type WriteOpts = BackgroundableOpts & {
  replay?: boolean;
  backgroundPhotos?: boolean;
  /**
   * Called as soon as the server has accepted the save, before any local
   * (IndexedDB) work that can still fail, so a background job's Retry knows not
   * to send the save again (the API adds a note for every `comments` it gets).
   */
  onServerSaved?: (saved: any) => void;
};

/**
 * A job's progress covers all its photos; one request inside it covers `part`
 * of them, starting after `offset`. Without this each request reported 0→100%
 * of the whole job and the notice jumped backwards between requests.
 */
function scaledProgress(
  onProgress: UploadProgress | undefined,
  offset: number,
  part: number,
  total: number,
): UploadProgress | undefined {
  if (!onProgress || total <= 0) return onProgress;

  return (fraction) => onProgress((offset + fraction * part) / total);
}

/** New photos in a form value, by the same rule extractUploads uses. */
function countNewPhotos(images: IUpload[] | undefined): number {
  return images && images.length ? extractUploads(images)[0].length : 0;
}

function keepExistingPhotos(images: IUpload[] | undefined): IUpload[] {
  return images && images.length ? extractUploads(images)[1] : [];
}

/** Only online, not on a replay, and only when there is something to upload. */
function shouldBackground(opts: WriteOpts, newPhotos: number): boolean {
  return (
    !!opts.backgroundPhotos && !opts.replay && navigator.onLine && newPhotos > 0
  );
}

/**
 * The offline-queue step for a write. A background call (`localOnlyWhenOffline`)
 * gets a network-shaped failure instead: withOfflineWrite takes its offline
 * branch on `!navigator.onLine` before it looks at that option, so a connection
 * lost mid-job (during the resize, say) would otherwise queue into an offline
 * store that, for an inspection never downloaded, does not exist, and the job
 * would report the photos as uploaded.
 */
function offlineQueueOr<T>(opts: BackgroundableOpts, queue: () => Promise<T>) {
  if (!opts.localOnlyWhenOffline) return queue;

  return (): Promise<T> => {
    const err: any = new Error('No connection.');

    err.request = {};
    err.notSent = true;

    return Promise.reject(err);
  };
}

/**
 * The save before a background upload failed on the network. Only an error
 * that never left the device (`notSent`) proves the save did not happen, and
 * only then may it fall back to the normal path (which queues it offline).
 * Otherwise the request may have been applied with only its response lost:
 * sending it again would create the finding twice (or add its comment twice),
 * so stop and let the user check. The message is response-shaped because the
 * forms show `err.response.data.message`.
 */
function sentButUnconfirmed(err: any) {
  if (!isNetworkFailure(err) || err.notSent) return err;

  const unconfirmed: any = new Error('Save not confirmed.');

  unconfirmed.response = {
    data: {
      message:
        'the connection dropped before the save was confirmed. It may have been saved: check before saving again.',
    },
  };

  return unconfirmed;
}

/** The finding as the server has it now. Network only: show() falls back to the local copy. */
const fetchServerFinding = (id: number) =>
  instance.get(`/api/findings/${id}`).then((response) => response.data);

/** What a finding save leaves behind that a second copy of it would duplicate. */
type SaveTraces = { comment?: string; completing?: boolean; newActionNote?: boolean };

/** Whether this save would leave a trace on a finding as it was before it. */
function canShowTrace(before: any, traces: SaveTraces) {
  return Boolean(
    (traces.comment || '').trim() ||
      (traces.completing && !before.date_completed) ||
      traces.newActionNote,
  );
}

function saveLanded(before: any, now: any, traces: SaveTraces) {
  const comment = (traces.comment || '').trim();

  if (comment) {
    const seen = new Set((before.commentables || []).map((note: any) => note.id));

    if (
      (now.commentables || []).some(
        (note: any) => !seen.has(note.id) && (note.content || '').trim() === comment,
      )
    ) {
      return true;
    }
  }

  if (traces.completing && !before.date_completed && now.date_completed) return true;

  return Boolean(traces.newActionNote && now.action_note_id !== before.action_note_id);
}

/**
 * SW2-2458: a background save whose request went out and whose answer was lost
 * may have been applied. The API has no idempotency key, and it adds a note for
 * every `comments` it is sent (an action note too, for `actionObservation`
 * without `actionId`), so a blind Retry would post them twice. This snapshots
 * the findings before the first send; on a Retry after a lost answer it reads
 * them again and reports the save as landed when it left its traces (a new note
 * with the comment, a completion date that was not there, a new action note).
 * A save with none of those to leave duplicates nothing, so it is sent again.
 */
function lostSaveCheck(findingIds: number[], traces: SaveTraces) {
  let before: any[] | null = null;
  let lost = false;

  const read = () =>
    Promise.all(findingIds.map(fetchServerFinding)).catch((err: any) => {
      // Only reads: nothing was sent, so it may be retried automatically.
      if (isNetworkFailure(err)) err.notSent = true;

      throw err;
    });

  return {
    /** The findings as saved when a lost save turns out to have landed; otherwise null (send it). */
    async landed(): Promise<any[] | null> {
      if (lost && before) {
        const now = await read();

        lost = false;

        // Every finding that could show a trace must show it: a bulk close is
        // one request, so a partial match is not proof that it landed.
        const traceable = now
          .map((finding, i) => ({ finding, was: before![i] }))
          .filter(({ was }) => canShowTrace(was, traces));

        return traceable.length > 0 &&
          traceable.every(({ finding, was }) => saveLanded(was, finding, traces))
          ? now
          : null;
      }

      if (!before) before = await read();

      return null;
    },
    /** After the send failed. */
    failed(err: any) {
      if (isNetworkFailure(err) && !err.notSent) lost = true;
    },
  };
}

/**
 * A finding with a background job still pending must not get a second one:
 * the jobs run one after another, so both would upload the photos and post the
 * save (and its comment) twice. The finding still looks open until a closure
 * job finishes, so a repeat Complete is easy to do.
 */
function assertNoPendingJob(findingIds: number[]) {
  const busy = findingIds.filter(
    (id) => backgroundPhotoUploads.pending({ findingId: Number(id) }).length > 0,
  );

  if (busy.length > 0) {
    throw new Error(
      busy.length === 1
        ? "This finding's photos are still uploading. Try again once they finish."
        : `Photos for ${busy.length} of these findings are still uploading. Try again once they finish.`,
    );
  }
}

/** Queue the photo upload for a finding that is already saved. */
function queuePhotoUpload(
  inspectionId: number,
  finding: { id: number },
  images: IUpload[],
  photoType: 'initial' | 'closure',
) {
  backgroundPhotoUploads.enqueue({
    kind: 'finding',
    inspectionId: Number(inspectionId),
    findingId: finding.id,
    photoCount: countNewPhotos(images),
    run: async (onProgress) => {
      assertOnline();

      // The save that queued this job synced the existing photos already.
      const result = await uploadImages(finding, images, true, photoType, {
        onProgress,
        localOnlyWhenOffline: true,
        existingSynced: true,
      });

      markUploaded(images, result.finding_uploads);

      return result;
    },
  });
}

/**
 * Creates a finding record offline for an inspection
 * @param inspectionId - The ID of the inspection
 * @param formData - Form data containing finding details and images
 * @returns The newly created finding object
 */
async function createFindingOffline(
  inspectionId: number,
  formData: { [k: string]: any; images: IUpload[] },
  online: boolean = false,
  id: number = 0,
): Promise<any> {
  const { images, ...data } = formData;
  const user = await db.user.get(1);

  if (!user) {
    return;
  }

  // Create commentable objects if comments exist
  const commentableObj = data.comments
    ? [
        {
          author: {
            name: user!.name,
            id: user!.user_id,
            first_name: user!.first_name,
            last_name: user!.last_name,
          },
          content: data.comments,
          observation_type: 'initial',
          created_at: new Date().toISOString(),
        },
      ]
    : [];

  // Get inspection details
  const inspection = await db.inspections.get(inspectionId);

  if (!inspection) {
    return;
  }

  const templateId = inspection!.template_id;
  const inspectionFindings = inspection!.findings;

  // Get existing findings and certification
  const findings = await db.findings
    .where('id')
    .anyOf(inspectionFindings.map((f: any) => f.id))
    .toArray();
  const existingCertification = await db.certifications.get(inspectionId);

  if (!existingCertification || !findings) {
    console.log(
      'No existing certification found for inspection:',
      inspectionId,
    );
    return;
  }

  // Get finding type details
  const types = await db.findingTypeInspectionTemplate.get(Number(templateId));
  const findingType = types.findingTypes.find(
    (ft: any) => ft.name === data.type,
  );
  const findingTypeObj = {
    is_static_type: findingType.is_static_type,
    is_required_for_cert: findingType.details.is_required_for_cert,
    finding_type_id: findingType.id,
    color: findingType.details.color,
    is_enabled: findingType.details.is_enabled,
  };

  // Format dates
  const date_initiated = getDateOnly(data.date_initiated) || null;
  const date_completed = data.date_completed
    ? getDateOnly(data.date_completed)
    : null;
  const { comments, ...rest } = data;

  // Create finding object
  const findingObj = {
    id: generateId(id, findings),
    InspectionFindingModel: {
      carried_over: false,
      inspection_id: inspectionId,
    },
    ...rest,
    ...findingTypeObj,
    inspection_id: inspectionId,
    date_initiated,
    number:
      findings && findings.length
        ? findings[findings.length - 1].number + 1
        : 1,
    commentables: commentableObj,
    date_completed,
    sync: {
      synced: false,
      payload: [
        {
          method: 'create',
          ...data,
        },
      ],
    },
  };

  if (online) {
    findingObj.sync.synced = true;
    findingObj.sync.payload = [];
  }
  // Add finding to database
  const findingId = await db.findings.add(findingObj).catch((error: any) => {
    console.log('Error creating offline finding', error);
    return Promise.reject(error);
  });

  // Update inspection with new finding
  await db.inspections
    .update(inspectionId, {
      findings: [...inspectionFindings, findingObj],
    })
    .catch((error: any) => {
      console.log('Error creating offline finding', error);
      return Promise.reject(error);
    });

  // Return the newly created finding
  const finding = await db.findings.get(findingId).catch((error: any) => {
    console.log('Error creating offline finding', error);
    return Promise.reject(error);
  });

  // // Update certification if all required findings are completed
  const requiredFindings = [...findings, finding].filter(
    (f: any) => f.is_required_for_cert,
  );
  const completedRequiredFinings = requiredFindings.filter(
    (f: any) => f.date_completed,
  );

  if (
    requiredFindings.length === completedRequiredFinings.length &&
    existingCertification.groupOptions['compliance'] &&
    existingCertification.groupOptions['compliance'].errors.length === 0
  ) {
    existingCertification.groupOptions['compliance'].enabled = true;
    existingCertification.groupOptions['compliance'].errors = [];

    await db.certifications
      .update(inspectionId, existingCertification)
      .catch((error: any) => {
        console.log('Error updating offline certification', error);
        return Promise.reject(error);
      });
  } else if (existingCertification.groupOptions['compliance']) {
    existingCertification.groupOptions['compliance'].enabled = false;
    if (
      existingCertification.groupOptions['compliance'].errors.indexOf(
        'Findings need to be completed prior to signing',
      ) === -1
    ) {
      existingCertification.groupOptions['compliance'].errors.push(
        'Findings need to be completed prior to signing',
      );
    }

    await db.certifications
      .update(inspectionId, existingCertification)
      .catch((error: any) => {
        console.log('Error updating offline certification', error);
        return Promise.reject(error);
      });
  }

  return { ...finding, dissolveFindingImages: false };
}

/**
 * Updates a finding record in an offline database and handles related records
 * @param id The ID of the finding to update
 * @param formData Form data containing finding details and images
 * @returns The updated finding object
 */
async function updateFindingOffline(
  id: number,
  formData: { [k: string]: any; images: IUpload[] },
  online: boolean = false,
): Promise<any> {
  const { images, ...data } = formData;

  try {
    // Get current user
    const user = await db.user.get(1);

    // Get existing finding
    const oldFinding = await db.findings.get(id);

    if (!oldFinding || !user) {
      // Expected when the inspection was never synced for offline use — the
      // offline mirror is skipped and the online flow is unaffected.
      console.log(
        'Offline mirror: finding not in local db, skipping offline update:',
        id,
      );
      return;
    }

    // Process comments if provided
    const commentableObj: any[] = data.comments
      ? [
          {
            author: {
              name: user!.name,
              id: user!.user_id,
              first_name: user!.first_name,
              last_name: user!.last_name,
            },
            content: data.comments,
            observation_type: data.date_completed ? 'final' : 'progress',
            created_at: new Date().toISOString(),
          },
        ]
      : [];

    // Combine existing and new comments
    const updatedCommentables = [...oldFinding.commentables, ...commentableObj];

    // Format dates
    const date_initiated = data.date_initiated
      ? getDateOnly(data.date_initiated)
      : null;
    const date_completed = data.date_completed
      ? getDateOnly(data.date_completed)
      : null;

    const existingFinding = await db.findings.get(id);

    const { comments, ...rest } = data;

    const edit: { [k: string]: any } = {
      method: 'update',
      ...data,
      type: data.bulk ? existingFinding.type : data.type,
      date_initiated: data.bulk ? existingFinding.date_initiated : date_initiated,
      location: data.bulk ? existingFinding.location : data.location,
    };

    // SW2-2446: what an online save puts behind the queue — only what it overwrites. The server
    // adds a note for every `comments` it is sent, and an action note for an `actionObservation`
    // without `actionId`, and it already has those from this save, so a replay would add them
    // twice. A save that only adds notes (addNotes) then has nothing to queue.
    const { comments: _addedNote, ...onlineEdit } = edit;

    if (!data.actionId) delete onlineEdit.actionObservation;

    const overwrites = Object.keys(data).some(
      (key) => !['comments', 'actionObservation', 'inspectionId', 'bulk', 'completedImages'].includes(key),
    );

    // Update finding record. SW2-2446: an online edit keeps any edit queued offline before it, and
    // is queued behind it so the sync does not replay the older edit over this one.
    await db.findings.update(id, {
      ...rest,
      date_initiated: data.bulk
        ? existingFinding.date_initiated
        : date_initiated,
      date_completed,
      commentables: updatedCommentables,
      sync: online
        ? syncAfterOnlineSave(existingFinding.sync, overwrites ? onlineEdit : undefined)
        : {
            // SW2-2443: keep what the offline sync records on the row (`serverId`);
            // replacing the object dropped it, so a queued edit lost its server id.
            ...existingFinding.sync,
            synced: false,
            payload: [...(existingFinding.sync?.payload || []), edit],
          },
    });

    // Get inspection and related findings
    const inspection = await db.inspections.get(Number(data.inspectionId));

    if (!inspection) {
      console.log('Inspection not found for update:', data.inspectionId);
      return;
    }
    const inspectionFindings = inspection!.findings;
    const findings = await db.findings
      .where('id')
      .anyOf(inspectionFindings.map((f: any) => f.id))
      .toArray();

    // Check if all required findings are completed
    const requiredFindings = findings.filter(
      (f: any) => f.is_required_for_cert && f.id !== id,
    );
    const completedRequiredFindings = requiredFindings.filter(
      (f: any) => f.date_completed,
    );

    // Update certification if all required findings are completed
    if (requiredFindings.length === completedRequiredFindings.length) {
      const existingCertification = await db.certifications.get(
        Number(data.inspectionId),
      );

      if (!existingCertification) {
        console.log(
          'Certification not found for inspection:',
          data.inspectionId,
        );
        return;
      }

      existingCertification.groupOptions['compliance'].enabled = true;
      existingCertification.groupOptions['compliance'].errors = [];
      existingCertification.minCertificationDate =
        date_completed &&
        date_completed > existingCertification.minCertificationDate
          ? date_completed
          : existingCertification.minCertificationDate;

      await db.certifications.update(
        Number(data.inspectionId),
        existingCertification,
      );
    } else {
      const existingCertification = await db.certifications.get(
        Number(data.inspectionId),
      );

      existingCertification.groupOptions['compliance'].enabled = false;
      if (
        existingCertification.groupOptions['compliance'].errors.indexOf(
          'Findings need to be completed prior to signing',
        ) === -1
      ) {
        existingCertification.groupOptions['compliance'].errors.push(
          'Findings need to be completed prior to signing',
        );
      }

      await db.certifications.update(
        Number(data.inspectionId),
        existingCertification,
      );
    }

    // Get updated finding
    const finding = await db.findings.get(id);

    // Update inspection with the updated finding
    await db.inspections.update(Number(data.inspectionId), {
      findings: [
        ...inspection!.findings.filter((f: any) => f.id !== id),
        finding,
      ],
    });

    return { ...finding, dissolveFindingImages: false };
  } catch (error) {
    console.log('Error updating offline finding', error);
    return Promise.reject(error);
  }
}

function extractUploads(
  images: IUpload[],
  {
    dropAppliedEdits = false,
  }: {
    /**
     * SW2-2458. For a background job's Retry: a photo it already uploaded had its rotation and
     * annotations applied when the server inserted it, and an existing photo's edits were applied
     * when its request succeeded (appliedExistingEdits). The server applies them again to an
     * existing upload (a rotation is relative to the stored file; an annotation without an id is
     * a new row). Not for a form's retry, where the user may have changed the photo since.
     */
    dropAppliedEdits?: boolean;
  } = {},
): [IUploadWithFile[], IExistingUpload[]] {
  const newUploads: IUploadWithFile[] = [];
  const existingUploads: IExistingUpload[] = [];

  images.forEach((image) => {
    const uploadedId =
      isUploadWithFile(image) && image.guid ? uploadedPhotoIds.get(image.guid) : undefined;

    if (uploadedId !== undefined) {
      // Already on the server (see uploadedPhotoIds): sent as the existing upload it now is.
      const { file, ...rest } = image as any;

      existingUploads.push({
        ...rest,
        id: uploadedId,
        ...(dropAppliedEdits ? { rotation: 0, annotations: [] } : {}),
      } as IExistingUpload);
    } else if (isUploadWithFile(image)) {
      newUploads.push(image);
    } else if (isExistingUpload(image)) {
      existingUploads.push(
        dropAppliedEdits && appliedExistingEdits.has(image)
          ? ({ ...image, rotation: 0, annotations: [] } as IExistingUpload)
          : image,
      );
    }
  });

  return [newUploads, existingUploads];
}

export const findingApi = {
  index: async (id: number, excludeCompleted: boolean = false) =>
    // SW2-2321: converted from a `navigator.onLine` pre-check. After an offline
    // reload the findings list was empty even though the records were cached.
    withOfflineFallback<any>(
      () =>
        instance
          .get(url(id), { params: { excludeCompleted } })
          .then((response) => response.data),
      async () => {
        const inspection = await db.inspections.get(Number(id));

        // No cached inspection means no cached answer. Falling through to
        // `anyOf([])` returned `[]`, which withOfflineFallback treats as a hit
        // and reports as "this inspection has no findings" — hiding the request
        // failure. An inspection that *is* cached and genuinely has no findings
        // must still return `[]`, because that is a real answer.
        // (Raised by Copilot on PR #53.)
        if (!inspection) {
          return null;
        }

        const findingIds = inspection.findings.map((f: any) => f.id);

        return db.findings.where('id').anyOf(findingIds).toArray();
      },
    ),

  show: async (id: number): Promise<Finding> =>
    // SW2-2321: converted from a `navigator.onLine` pre-check. After an offline
    // reload opening a finding hit the network and failed rather than reading the
    // cached record.
    withOfflineFallback<Finding>(
      () =>
        instance.get(`/api/findings/${id}`).then((response) => response.data),
      () => db.findings.get(Number(id)) as Promise<Finding | undefined>,
    ),

  create: async (
    inspectionId: number,
    formData: { [k: string]: any; images: IUpload[] },
    // SW2-2321: `opts.replay` must be passed when this call is the flush of an
    // already-queued write, so an unreachable server rejects instead of
    // re-queueing a write the sync loop is about to mark as synced.
    opts: WriteOpts = {},
  ): Promise<any> => {
    const { images, ...data } = formData;

    // SW2-2458: save the finding now and upload its photos in the background,
    // so the form can close without waiting on the upload.
    if (shouldBackground(opts, countNewPhotos(images))) {
      let saved: any;
      let serverSaved: any = null;

      try {
        saved = await findingApi.create(
          inspectionId,
          { ...formData, images: [] },
          {
            localOnlyWhenOffline: true,
            onServerSaved: (finding) => {
              serverSaved = finding;
            },
          },
        );
      } catch (err) {
        if (serverSaved) {
          // The finding is on the server; only the local mirror write after it
          // failed. Its photos must still be queued, or they are lost when the
          // form closes.
          saved = { ...serverSaved, finding_uploads: [] };
        } else if (isNetworkFailure(err) && err.notSent) {
          // Offline before the save was sent: fall back to the normal path,
          // which queues the finding AND its photos for offline sync.
          return findingApi.create(inspectionId, formData);
        } else {
          throw sentButUnconfirmed(err);
        }
      }

      if (saved.dissolveFindingImages === false) {
        queuePhotoUpload(inspectionId, saved, images, 'initial');
      }

      return saved;
    }

    // SW2-2321: was a `navigator.onLine` pre-check. After an offline reload the
    // POST was attempted, failed, and createFindingOffline never ran — the
    // finding and its photos were lost with no error and nothing queued.
    return withOfflineWrite<any>(
      async () => {
        const newFinding = await instance
          .post(url(inspectionId), data)
          .then((response) => response.data);

        if (opts.onServerSaved) opts.onServerSaved(newFinding);

        // SW2-2321: skipped on replay. A replay is the flush of a row that is
        // ALREADY in IndexedDB, and these mirrors are create-style — they append
        // a second copy beside it (and `createOfflineCertification(..., true)`
        // also clears the entire remaining payload array, losing every signature
        // after the first). Replay must be wire-only: the sync loop reconciles
        // the local row itself. (Raised by Copilot on PR #53.)
        if (!opts.replay) {
          await createFindingOffline(
            inspectionId,
            formData,
            true,
            newFinding.id,
          );
        }

        return newFinding.dissolveFindingImages === false
          ? await uploadImages(newFinding, images, true, 'initial', opts)
          : { ...newFinding, finding_uploads: [] };
      },
      // The previous offline branch called `.catch` on a plain object literal
      // — `{ ...finding, finding_uploads: [] }.catch(...)` — which is a
      // TypeError, not a no-op, so any finding created offline without
      // dissolved images threw here. Restructured so the catch wraps the
      // promise chain instead.
      offlineQueueOr(opts, () =>
          createFindingOffline(inspectionId, formData)
            .then((finding: any) =>
              finding.dissolveFindingImages === false
                ? uploadImages(finding, images)
                : { ...finding, finding_uploads: [] },
            )
            .catch((error: any) => {
              console.log('Error creating offline finding', error);

              return Promise.reject(error);
            }),
        ),
      opts,
    );
  },

  destroy: (id: number, note: string) =>
    // SW2-2321: was a `navigator.onLine` pre-check. After an offline reload the
    // DELETE was attempted, failed, and the local delete never ran — so a finding
    // the inspector had deleted reappeared.
    withOfflineWrite<any>(
      () =>
        instance
          .delete(`/api/findings/${id}`, { data: { note: note } })
          .then((response) => response.data),
      () =>
        db.findings
          .delete(id)
          .then(() => {
            return db.findings
              .where('id')
              .equals(id)
              .toArray()
              .then((findings: any) => {
                return findings;
              });
          })
          .catch((error: any) => {
            console.log('Error deleting offline finding', error);
            return Promise.reject(error);
          }),
      // SW2-2321: `localOnlyWhenOffline` because nothing replays this local
      // write — the sync loop handles only create/update payloads. Resolving on
      // an unreachable server would claim the change will sync when it never
      // will, and the next download would restore the server's version.
      // (Raised by Copilot on PR #53.)
      { localOnlyWhenOffline: true },
    ),

  update: async (
    id: number,
    formData: { [k: string]: any; images: IUpload[] },
    // SW2-2321: `opts.replay` must be passed when this call is the flush of an
    // already-queued write, so an unreachable server rejects instead of
    // re-queueing a write the sync loop is about to mark as synced.
    opts: WriteOpts = {},
  ): Promise<any> => {
    const { images, completedImages, ...data } = formData;

    // SW2-2458: return now and finish in the background when there are new photos.
    const newClosure = countNewPhotos(completedImages);
    const newInitial = countNewPhotos(images);

    if (shouldBackground(opts, newClosure + newInitial)) {
      const inspectionId = Number(formData.inspectionId);

      assertNoPendingJob([Number(id)]);

      if (newClosure > 0) {
        // The save is what closes the finding, and the server refuses a close
        // until its closure photos exist, so the whole save runs in the job, in
        // the usual order (closure photos, save, initial photos).
        //
        // Two steps, so a Retry after the save went through only redoes the
        // photos: the server adds a note for every `comments` it is sent (and a
        // new action note for an `actionObservation` without `actionId`), so
        // re-running the save would post them twice. Closure photos are already
        // protected by markUploaded.
        let saved: any = null;
        const lostSave = lostSaveCheck([Number(id)], {
          comment: data.comments,
          completing: Boolean(data.date_completed),
          newActionNote: Boolean(data.actionObservation && !data.actionId),
        });

        backgroundPhotoUploads.enqueue({
          kind: 'finding',
          inspectionId,
          findingId: Number(id),
          photoCount: newClosure + newInitial,
          run: async (onProgress) => {
            assertOnline();

            // Step 1 sends no initial photos, so after the save nothing in it
            // talks to the server: a failure there cannot leave the save sent
            // but unrecorded (only the local mirror write follows it).
            const total = newClosure + newInitial;

            if (!saved) {
              const landed = await lostSave.landed();

              // The response with the photo-dissolving flag was lost; the
              // initial photos the user added are uploaded rather than lost.
              if (landed) saved = { ...landed[0], dissolveFindingImages: false };
            }

            if (!saved) {
              try {
                const result = await findingApi.update(
                  id,
                  { ...formData, images: [] },
                  {
                    onProgress: scaledProgress(onProgress, 0, newClosure, total),
                    localOnlyWhenOffline: true,
                    // Checkpoint the save the moment the server accepts it, even
                    // if the local mirror write after it then fails.
                    onServerSaved: (finding) => {
                      saved = finding;
                    },
                  },
                );

                saved = saved || result;
              } catch (err) {
                if (!saved) lostSave.failed(err);

                throw err;
              }
            }

            // Step 2: the finding's initial photos, existing and new, so a
            // removed or rotated existing photo still syncs.
            if (images && images.length > 0 && saved.dissolveFindingImages === false) {
              const result = await uploadImages(saved, images, true, 'initial', {
                onProgress: scaledProgress(onProgress, newClosure, newInitial, total),
                localOnlyWhenOffline: true,
              });

              markUploaded(images, result.finding_uploads);
            }

            return saved;
          },
        });

        return { id, backgrounded: true };
      }

      // Only initial photos: save the edit now with the photos already on the
      // server, then upload the new ones.
      let saved: any;
      let serverSaved: any = null;

      try {
        saved = await findingApi.update(
          id,
          { ...formData, images: keepExistingPhotos(images) },
          {
            localOnlyWhenOffline: true,
            onServerSaved: (finding) => {
              serverSaved = finding;
            },
          },
        );
      } catch (err) {
        if (serverSaved) {
          // The edit is on the server; only the local mirror write after it
          // failed. Its new photos must still be queued, or they are lost when
          // the form closes.
          saved = serverSaved;
        } else if (isNetworkFailure(err) && err.notSent) {
          return findingApi.update(id, formData);
        } else {
          throw sentButUnconfirmed(err);
        }
      }

      if (saved.dissolveFindingImages === false) {
        queuePhotoUpload(inspectionId, saved, images, 'initial');
      }

      return saved;
    }

    // SW2-2321: was a `navigator.onLine` pre-check. After an offline reload the
    // POST was attempted, failed, and updateFindingOffline never ran — the edit
    // and any new photos were lost silently.
    // Set once the closure photos have reached the server, so that if the save
    // then fails on the network, the offline fallback does not queue them a
    // second time (a photo is only "existing" to extractUploads once it has an
    // id, and a successful upload sets only its guid).
    let closureUploadedOnline = false;

    return withOfflineWrite<any>(
      async () => {
        // SW2-2136: closure photos go up BEFORE the finding is saved. The save
        // is what closes the finding, and the server now refuses a close with
        // no closure photo when the client requires one — so a photo added in
        // the same save has to exist by then, as it already does on the bulk
        // path (CompleteFindingsModal uploads first). The finding exists, so
        // its id is all the upload needs. With photo dissolving on, the server
        // deletes these as the finding closes, the same end state as before.
        //
        // Not on a replay: the offline sync uploads the queued photos, with
        // their type, before it replays the save (InspectionsDue
        // syncPendingFindingPhotos), so uploading here as well would duplicate
        // them, and a File iOS has since reclaimed would stop the save syncing.
        const closureResult = opts.replay
          ? { finding_uploads: [] }
          : await uploadImages({ id }, completedImages, true, 'closure', opts);

        closureUploadedOnline = !opts.replay;

        // Mark what just went up as existing (an id makes a photo "existing"
        // to extractUploads), so a retry after a refused save — a signed
        // inspection, an invalid type — does not upload the same photos again.
        markUploaded(completedImages, closureResult.finding_uploads);

        const updatedFinding = await instance
          .post(`/api/findings/${id}`, data)
          .then((response) => response.data);

        if (opts.onServerSaved) opts.onServerSaved(updatedFinding);

        // SW2-2321: awaited (it was fire-and-forget, so the request callback
        // resolved before IndexedDB was written and a rejection became an
        // unhandled promise) and skipped on replay, where the row already
        // exists. (Raised by Copilot on PR #53.)
        if (!opts.replay) {
          await updateFindingOffline(id, formData, true);
        }
        if (updatedFinding.dissolveFindingImages === false) {
          const initialResult = await uploadImages(
            updatedFinding,
            images,
            true,
            'initial',
            opts,
          );

          // Each online upload call returns the finding's COMPLETE upload list
          // (initial + closure) from the server. The initial call now runs
          // last, so its response is the authoritative snapshot whenever it hit
          // the server; fall back to the closure call's when there were no new
          // initial photos (an empty image list short-circuits to []).
          return {
            ...updatedFinding,
            finding_uploads:
              initialResult.finding_uploads.length > 0
                ? initialResult.finding_uploads
                : closureResult.finding_uploads,
          };
        }
        return { ...updatedFinding, finding_uploads: [] };
      },
      offlineQueueOr(opts, () =>
        updateFindingOffline(id, formData)
          .then((finding: any) => {
            if (finding.dissolveFindingImages === false) {
              return uploadImages(finding, images, false, 'initial').then(() =>
                uploadImages(
                  finding,
                  closureUploadedOnline ? [] : completedImages,
                  false,
                  'closure',
                ),
              );
            }
            return { ...finding, finding_uploads: [] };
          })
          .catch((error: any) => {
            console.log('Error updating offline finding', error);

            return Promise.reject(error);
          }),
      ),
      opts,
    );
  },

  bulk: {
    complete: async (
      inspectionId: number,
      data: {
        [imageKeyName: string]: IUpload[];
      } & { finding_ids: number[]; date_completed: Date; comments: string },
      opts: WriteOpts = {},
    ): Promise<any> => {
      // SW2-2458: closure photos must be on the server before the findings are
      // closed, so with new photos the whole completion runs in the background.
      const newPhotos = (data.finding_ids || []).reduce(
        (sum, id) => sum + countNewPhotos(data[`images_${id}`] as IUpload[]),
        0,
      );

      if (shouldBackground(opts, newPhotos)) {
        assertNoPendingJob(data.finding_ids || []);

        let closedOnServer = false;
        const lostSave = lostSaveCheck(data.finding_ids, {
          comment: data.comments,
          completing: Boolean(data.date_completed),
        });

        backgroundPhotoUploads.enqueue({
          kind: 'finding',
          inspectionId: Number(inspectionId),
          findingId: data.finding_ids[0],
          findingIds: data.finding_ids,
          photoCount: newPhotos,
          run: async (onProgress) => {
            assertOnline();

            // The close already reached the server on an earlier attempt (only
            // the local mirror failed): sending it again would add the
            // comment to every finding twice.
            if (closedOnServer) return { backgrounded: false };

            // Or its answer was lost but it was applied (see lostSaveCheck).
            if (await lostSave.landed()) {
              closedOnServer = true;

              return { backgrounded: false };
            }

            try {
              return await findingApi.bulk.complete(inspectionId, data, {
                onProgress,
                localOnlyWhenOffline: true,
                onServerSaved: () => {
                  closedOnServer = true;
                },
              });
            } catch (err) {
              if (!closedOnServer) lostSave.failed(err);

              throw err;
            }
          },
        });

        return { backgrounded: true };
      }

      const {
        finding_ids: findingIds,
        date_completed: dateCompleted,
        comments,
        completed_by,
      } = data;
      const findingUploads = new Map<number, IUpload[]>();

      findingIds.forEach((id) => {
        const imagesKey = `images_${id}`;

        if (Object.prototype.hasOwnProperty.call(data, imagesKey)) {
          findingUploads.set(id, data[imagesKey]);
        }
      });

      // SW2-2136: photos added in Complete Findings are closure photos, and they
      // go up BEFORE the findings are closed. They used to be uploaded after the
      // PUT, and as 'initial', so with closure photos required the server found
      // none and refused the close, and the photos printed as Initial.
      let photosUploadedOnline = false;

      // SW2-2321: was a `navigator.onLine` pre-check. Bulk-completing findings
      // after an offline reload sent the PUT, failed, and never ran
      // updateFindingOffline — losing the completion for every finding selected.
      return withOfflineWrite<any>(
        async () => {
          const uploadResults = new Map<number, any[]>();
          let photosSent = 0;

          for (const findingId of findingIds) {
            const photos = findingUploads.get(findingId) || [];
            const count = countNewPhotos(photos);
            const result = await uploadImages(
              { id: findingId },
              photos,
              true,
              'closure',
              {
                ...opts,
                onProgress: scaledProgress(opts.onProgress, photosSent, count, newPhotos),
              },
            );

            photosSent += count;

            uploadResults.set(findingId, result.finding_uploads || []);
            markUploaded(photos, result.finding_uploads);
          }

          photosUploadedOnline = true;

          const {
            data: findings,
          }: { data: { id: number; dissolveFindingImages: boolean }[] } =
            await xhrService.put(`/api/inspections/${inspectionId}/findings`, {
              finding_ids: findingIds,
              date_completed: dateCompleted,
              comments,
              completed_by,
            });

          if (opts.onServerSaved) opts.onServerSaved(findings);

          for (const findingId of findingIds) {
            const formData = {
              date_completed: dateCompleted,
              comments: comments,
              images: findingUploads.get(findingId) || [],
              completedImages: [],
              inspectionId: inspectionId,
              bulk: true,
            };

            await updateFindingOffline(findingId, formData, true);
          }

          // Photos are already up. With dissolving on, the server deleted them as
          // it closed the findings, so there is nothing to show.
          if (findings[0] && findings[0].dissolveFindingImages === false) {
            return findings.map((finding) => ({
              ...finding,
              finding_uploads: uploadResults.get(finding.id) || [],
            }));
          }

          return findings;
        },
        offlineQueueOr(opts, async () => {
          for (const findingId of findingIds) {
            const formData = {
              date_completed: dateCompleted,
              comments: comments,
              images: findingUploads.get(findingId) || [],
              completedImages: [],
              inspectionId: inspectionId,
              bulk: true,
            };

            await updateFindingOffline(findingId, formData);
          }

          const findings = await db.findings
            .where('id')
            .anyOf(findingIds)
            .toArray();

          if (findings[0] && !findings[0].dissolveFindingImages) {
            return await Promise.all(
              findings.map((finding: any) =>
                uploadImages(
                  finding,
                  // Already on the server if the PUT failed after the uploads.
                  photosUploadedOnline ? [] : findingUploads.get(finding.id),
                  false,
                  'closure',
                ),
              ),
            );
          }

          return findings;
        }),
        opts,
      );
    },
    addNotes: async (
      inspectionId: number,
      findingIds: number[],
      comments: any,
    ) => {
      // SW2-2321: was a `navigator.onLine` pre-check. Notes added after an
      // offline reload were posted, failed, and never written locally.
      return withOfflineWrite<any>(
        async () => {
          const noteData = await xhrService.post(
            `/api/inspections/${inspectionId}/findings/notes`,
            {
              findingIds,
              comments,
            },
          );

          for (const findingId of findingIds) {
            const formData = {
              comments: comments,
              inspectionId: inspectionId,
              bulk: true,
              images: [],
              completedImages: [],
            };

            await updateFindingOffline(findingId, formData, true);
          }

          return noteData;
        },
        async () => {
          for (const findingId of findingIds) {
            const formData = {
              comments: comments,
              inspectionId: inspectionId,
              bulk: true,
              images: [],
              completedImages: [],
            };

            await updateFindingOffline(findingId, formData);
          }

          const inspection = await db.inspections.get(Number(inspectionId));
          const inspectionFindings = inspection ? inspection.findings : [];
          const findings = await db.findings
            .where('id')
            .anyOf(inspectionFindings.map((f: any) => f.id))
            .toArray();

          return { data: findings };
        },
      );
    },
  },
  complianceImpact: (
    findingId: number,
    type: string,
    inspectionId: number,
  ): Promise<{
    affected_inspections: Array<{
      id: number;
      created_date: string;
      type: string;
    }>;
  }> =>
    instance
      .get(`/api/findings/${findingId}/compliance-impact`, {
        params: { type, inspectionId },
      })
      .then((response) => response.data),

  addSignature: (actionId: number) => {
    return xhrService.get(`/api/findings/${actionId}/cert`);
  },
  removeSignature: (actionId: number) => {
    return xhrService.delete(`/api/findings/${actionId}/actionsign`);
  },
  deleteNotes: (findingId: number, actionId: number) => {
    return xhrService.delete(`/api/findings/${findingId}/cert/${actionId}`);
  },
};

export default findingApi;
